import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import Flex from '@Components/Bases/Flex';
import { CVUP_ROLE } from '@Domains/Profile';
import { usePublishReport } from '@Domains/ReviewSummaryReport';
import { useDisclosure } from '@hooks/useDisclosure';
import { convertBuddhistYearToGregorian, formatDate } from '@Utils/datetime';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { SectionEnum } from 'src/Enum/SectionEnum';
import ConfirmModal from '@Components/Modal/ConfirmModal';
import { useReviewSummaryReportForm } from './ReviewSummaryReportFormProvider/ReviewSummaryReportFormProvider';
import { useReviewSummaryReport } from './ReviewSummaryReportProvider/ReviewSummaryReportProvider';
import { checkValidOperatingYear } from './utils';

const PublishReport = () => {
  const { id } = useParams();
  const { user } = useProfile();
  const [{ loading, error }, publishReport] = usePublishReport(id);
  const { summaryData } = useReviewSummaryReport();
  const { refetchProgressWithUpdate, progressData } = useProgressBar();
  const { watch, setValue } = useReviewSummaryReportForm();
  const navigate = useNavigate();

  const [
    openedConfirmModal,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();

  const isDraftReport = watch('draftReportStatus');
  const isAllSectionsCompleted = watch('completedAllSection');
  const publishedDate = watch('publishedDate');

  const hasCommitteeApprovalDate = Boolean(summaryData?.committeeApprovalDate);
  const isApprover = user?.N_ROLES.some(
    (role) => role === CVUP_ROLE.CVUP_APPROVER
  );
  const isPublished = Boolean(publishedDate);

  const startOperatingYear = convertBuddhistYearToGregorian(
    progressData?.report?.startOperatingYear
  );

  const isValidOperatingYear = checkValidOperatingYear(startOperatingYear);

  const enablePublishReport =
    process.env.REACT_APP_TOGGLE_PUBLISH_REPORT === 'true';

  const handleReviewDraftReport = () => {
    const url = `/report/${id}/preview?section=${SectionEnum.Summary}`;
    window.open(url, '_blank');
  };

  const handleConfirmPublish = async () => {
    try {
      const response = await publishReport();
      const publishedDateResponse = response.data.publishedDate;

      if (response) {
        setValue(
          'publishedDate',
          publishedDateResponse ? publishedDateResponse : undefined
        );

        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      closeConfirmModal();
      openSnackbar();
      refetchProgressWithUpdate(false);
    }
  };

  return (
    <Box bgcolor={'white'} p={2}>
      <Typography fontWeight={800}>เผยแพร่รายงาน Corporate Value Up</Typography>
      <Typography mt={2}>
        เผยแพร่รายงาน Corporate Value Up ภาษาไทยและภาษาอังกฤษ
      </Typography>
      <Flex
        flexDirection={'column'}
        width={1}
        mt={2}
        justifyContent={'center'}
        alignItems={'center'}
        gap={2}
      >
        <Flex gap={2}>
          <Button
            color="inherit"
            onClick={handleReviewDraftReport}
            disabled={
              !isDraftReport ||
              !isAllSectionsCompleted ||
              !hasCommitteeApprovalDate ||
              !isValidOperatingYear ||
              isPublished
            }
          >
            ดูตัวอย่างก่อนเผยแพร่
          </Button>
          <Button
            onClick={openConfirmModal}
            disabled={
              !isDraftReport ||
              !isAllSectionsCompleted ||
              !hasCommitteeApprovalDate ||
              !isValidOperatingYear ||
              !isApprover ||
              !enablePublishReport ||
              isPublished
            }
          >
            เผยแพร่รายงาน
          </Button>
        </Flex>
        {isPublished && summaryData?.publishedDate && (
          <Typography bgcolor="primary.lighter" p={0.5}>
            เผยแพร่เมื่อ {formatDate(summaryData?.publishedDate, 'th-TH')}
          </Typography>
        )}
      </Flex>
      <ConfirmModal
        topic="ยืนยันการเผยแพร่รายงาน Corporate Value Up"
        description="ท่านต้องการยืนยันการเผยแพร่รายงาน Corporate Value Up ใช่หรือไม่ หากใช่กรุณากดยืนยัน"
        loading={loading}
        opened={openedConfirmModal}
        close={closeConfirmModal}
        confirm={handleConfirmPublish}
      />
      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!error ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {!!error ? 'เกิดข้อผิดพลาดในการเผยแพร่' : 'เผยแพร่สําเร็จ'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublishReport;
